import React, { useCallback, useEffect, useState } from 'react';
import { format, parse } from 'date-fns';
import { tr } from 'date-fns/locale';
import { api } from '../api/client';
import { useRegisterRefresh } from '../context/PullToRefreshContext';

const PERIODS = [
  { key: 'daily', label: 'Günlük' },
  { key: 'weekly', label: 'Haftalık' },
  { key: 'monthly', label: 'Aylık' },
  { key: 'custom', label: 'Özel Aralık' },
];

function StatusDot({ status, isOverdue }) {
  let color = 'bg-amber-400';
  if (isOverdue || status === 'overdue') color = 'bg-red-500';
  else if (status === 'completed') color = 'bg-green-500';
  return <span className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${color}`} title="" />;
}

function ReportTaskLine({ task }) {
  const isOverdue = task.is_overdue || task.status === 'overdue';
  const customerPart = task.customer_name ? `${task.customer_name} – ` : '';
  const suffix = task.leave_completion_suffix || '';
  return (
    <div className="flex items-start gap-2 py-1.5 text-sm">
      <StatusDot status={task.status} isOverdue={isOverdue} />
      <div className="min-w-0 flex-1">
        <span className="font-medium text-gray-900">
          #{task.code} {customerPart}
          {task.title}
        </span>
        {suffix && <span className="text-gray-700">{suffix}</span>}
        <span className="ml-2 align-middle">
          {task.status === 'completed' && (
            <span className="badge-completed">Tamamlandı</span>
          )}
          {task.status === 'in_progress' && !isOverdue && (
            <span className="badge-in-progress">Devam Ediyor</span>
          )}
          {(task.status === 'overdue' || isOverdue) && (
            <span className="badge-overdue">Süresi Geçmiş</span>
          )}
        </span>
      </div>
    </div>
  );
}

export default function ReportPage() {
  const [period, setPeriod] = useState('weekly');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pdfBusy, setPdfBusy] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);

  const buildParams = useCallback(() => {
    if (period === 'custom') {
      if (!customStart || !customEnd) return null;
      return { start_date: customStart, end_date: customEnd };
    }
    return { period };
  }, [period, customStart, customEnd]);

  const loadReport = useCallback(async () => {
    const params = buildParams();
    if (!params) {
      setReport(null);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data } = await api.getReport(params);
      setReport(data);
    } catch {
      setError('Rapor yüklenemedi.');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    if (period === 'custom' && (!customStart || !customEnd)) return;
    loadReport();
  }, [loadReport, period, customStart, customEnd]);

  const downloadPdf = async () => {
    const params = buildParams();
    if (!params) {
      setError('Özel aralık için başlangıç ve bitiş seçin.');
      return;
    }
    setPdfBusy(true);
    setError('');
    try {
      const res = await api.getReportPdf(params);
      const blob = res.data;
      let filename = 'haftalik-rapor.pdf';
      const cd = res.headers['content-disposition'];
      if (cd) {
        const m = cd.match(/filename="?([^";]+)"?/i);
        if (m) filename = m[1];
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      // blob response type olduğunda hata mesajı blob içinde geliyor, text olarak okuyalım
      let msg = 'PDF indirilemedi.';
      try {
        const errBlob = err.response?.data;
        if (errBlob instanceof Blob) {
          const text = await errBlob.text();
          const parsed = JSON.parse(text);
          msg = parsed.detail || text;
        } else if (err.response?.data?.detail) {
          msg = err.response.data.detail;
        } else if (err.message) {
          msg = `PDF indirilemedi: ${err.message}`;
        }
      } catch {
        // parse edilemedi, genel mesaj kalır
      }
      setError(msg);
    } finally {
      setPdfBusy(false);
    }
  };

  const previewPdf = async () => {
    const params = buildParams();
    if (!params) {
      setError('Özel aralık için başlangıç ve bitiş seçin.');
      return;
    }
    setPreviewBusy(true);
    setError('');
    try {
      const res = await api.getReportPdf(params);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      let msg = 'PDF önizleme açılamadı.';
      try {
        const errBlob = err.response?.data;
        if (errBlob instanceof Blob) {
          const text = await errBlob.text();
          const parsed = JSON.parse(text);
          msg = parsed.detail || text;
        }
      } catch { /* ignore */ }
      setError(msg);
    } finally {
      setPreviewBusy(false);
    }
  };

  const rangeLabel = () => {
    if (!report?.start_date || !report?.end_date) return '';
    const s = parse(String(report.start_date), 'yyyy-MM-dd', new Date());
    const e = parse(String(report.end_date), 'yyyy-MM-dd', new Date());
    return `${format(s, 'd MMMM yyyy', { locale: tr })} – ${format(e, 'd MMMM yyyy', { locale: tr })}`;
  };

  return (
    <div className="px-4 py-3 pb-24 space-y-4">
      <div className="flex flex-wrap gap-2">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => setPeriod(p.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              period === p.key
                ? 'bg-brand-500 text-white border-brand-500'
                : 'bg-white text-gray-600 border-gray-200'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {period === 'custom' && (
        <div className="card space-y-2">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Başlangıç</label>
            <input
              type="date"
              className="input"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Bitiş</label>
            <input
              type="date"
              className="input"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
            />
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          className="btn-secondary"
          onClick={previewPdf}
          disabled={previewBusy || (period === 'custom' && (!customStart || !customEnd))}
        >
          {previewBusy ? 'Açılıyor…' : 'PDF Önizle'}
        </button>
        <button
          type="button"
          className="btn-secondary"
          onClick={downloadPdf}
          disabled={pdfBusy || (period === 'custom' && (!customStart || !customEnd))}
        >
          {pdfBusy ? 'İndiriliyor…' : 'PDF İndir'}
        </button>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl p-3">{error}</div>
      )}

      {loading && (
        <div className="flex justify-center py-12">
          <div className="w-7 h-7 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {!loading && report && (
        <>
          <p className="text-xs text-gray-500">{rangeLabel()}</p>

          {/* Özet İstatistik Kartı */}
          {report.summary && (
            <div className="card space-y-2">
              <h3 className="text-sm font-semibold text-gray-700 mb-1">Dönem Özeti</h3>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-gray-50 rounded-xl px-3 py-2 flex items-center justify-between">
                  <span className="text-xs text-gray-500">Toplam İş</span>
                  <span className="text-base font-bold text-gray-900">{report.summary.total}</span>
                </div>
                <div className="bg-red-50 rounded-xl px-3 py-2 flex items-center justify-between">
                  <span className="text-xs text-red-600 font-medium">Geciken</span>
                  <span className="text-base font-bold text-red-600">{report.summary.overdue}</span>
                </div>
                <div className="bg-amber-50 rounded-xl px-3 py-2 flex items-center justify-between">
                  <span className="text-xs text-amber-600">Devam Eden</span>
                  <span className="text-base font-bold text-amber-600">{report.summary.in_progress}</span>
                </div>
                <div className="bg-green-50 rounded-xl px-3 py-2 flex items-center justify-between">
                  <span className="text-xs text-green-600">Tamamlanan</span>
                  <span className="text-base font-bold text-green-600">{report.summary.completed}</span>
                </div>
                {report.summary.deadline_revised > 0 && (
                  <div className="bg-slate-50 rounded-xl px-3 py-2 flex items-center justify-between col-span-2">
                    <span className="text-xs text-slate-500">Deadline Revize Edilen</span>
                    <span className="text-base font-bold text-slate-600">{report.summary.deadline_revised}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {(!report.data || report.data.length === 0) && (
            <p className="text-sm text-gray-400">Bu dönemde kayıt yok.</p>
          )}
          {report.data?.map((block) => (
            <section key={block.user_id} className="card">
              <h2 className="font-semibold text-gray-900 mb-2">{block.user_name}</h2>
              <div className="divide-y divide-gray-50">
                {block.tasks?.map((t) => (
                  <ReportTaskLine key={t.id} task={t} />
                ))}
              </div>
            </section>
          ))}
        </>
      )}
    </div>
  );
}
